import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { AuthLogo } from "@/components/Auth/AuthLogo";
import { TeamIcon } from "@/components/icons/TeamIcon";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function TeamInviteAccept() {
  usePageMeta("Team Invite — Shattara AI", "", { noIndex: true });
  const { token } = useParams<{ token: string }>();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<
    "loading" | "ready" | "accepting" | "error" | "success"
  >("loading");
  const [error, setError] = useState("");
  const [inviterName, setInviterName] = useState<string | null>(null);
  const [inviterSchool, setInviterSchool] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate(`/auth/signup?redirect=/team/invite/${token}`);
    } else if (user) {
      setStatus("ready");
      fetchInviteDetails();
    }
  }, [user, authLoading, token, navigate]);

  const fetchInviteDetails = async () => {
    if (!token) return;
    try {
      const { data, error } = await supabase.rpc("get_invite_details", {
        _token: token,
      });

      if (!error && data) {
        const details = data as { inviter_name?: string; inviter_school?: string };
        if (details.inviter_name) setInviterName(details.inviter_name);
        if (details.inviter_school) setInviterSchool(details.inviter_school);
      }
    } catch (err) {
      console.error("Error fetching invite details:", err);
    }
  };

  const handleAccept = async () => {
    setStatus("accepting");
    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        "team-invite-accept",
        { body: { token } }
      );

      if (fnError) {
        // Try to extract the actual error message from the response
        const errorMessage = data?.error || fnError.message || "Failed to accept invitation";
        setError(errorMessage);
        setStatus("error");
      } else if (data?.error) {
        setError(data.error);
        setStatus("error");
      } else {
        setStatus("success");
        setTimeout(() => navigate("/dashboard"), 2000);
      }
    } catch (err) {
      console.error("Accept invite error:", err);
      setError("Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  if (status === "loading" || authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 bg-background p-4">
        <div className="rounded-full bg-primary/10 p-4">
          <CheckCircle2 className="h-12 w-12 text-primary" />
        </div>
        <h1 className="text-2xl font-bold text-foreground">
          Welcome to the team!
        </h1>
        <p className="text-muted-foreground">Redirecting to dashboard...</p>
      </div>
    );
  }

  const inviteMessage = inviterName
    ? `You've been invited to a team by ${inviterName}${inviterSchool ? ` from ${inviterSchool}` : ""}`
    : "You've been invited to a team";

  return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-6 p-4 bg-background">
      <AuthLogo />
      <h1 className="text-2xl font-bold text-foreground text-center max-w-md">
        {inviteMessage}
      </h1>
      <p className="text-muted-foreground text-center max-w-md">
        Click below to join and start collaborating with your team on Shattara
        AI.
      </p>

      {error && (
        <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 px-4 py-2 rounded-lg max-w-md">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <Button
        onClick={handleAccept}
        disabled={status === "accepting"}
        size="lg"
        className="min-w-[160px] gap-1"
      >
        {status === "accepting" ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Joining...
          </>
        ) : (
          <>
            <TeamIcon size={18} />
            Join Team
          </>
        )}
      </Button>
    </div>
  );
}
